import { SlideChannelNormalizationResultSummary } from 'api/slideMultiplexChannelNormalizations';
import { compact, groupBy, includes, keys, map, max, min, orderBy, sortBy, sum } from 'es-toolkit/compat';

export interface MarkerNormalizationObservation {
  markerId: string;
  channelKey: string;
  slideId: string;
  experimentResultId: number;
  minValue: number;
  maxValue: number;
  dynamicRange: number;
  minZScore: number;
  maxZScore: number;
  rangeZScore: number;
  absoluteMaxZScore: number;
  isOutlier: boolean;
}

export interface MarkerNormalizationSummary {
  markerId: string;
  slideCount: number;
  minCurrentValue: number;
  maxCurrentValue: number;
  dynamicRangeMin: number;
  dynamicRangeMax: number;
  dynamicRangeMedian: number;
  dynamicRangeMean: number;
  dynamicRangeVariance: number;
  dynamicRangeStdDev: number;
  dynamicRangeLowerQuartile: number;
  dynamicRangeUpperQuartile: number;
  dynamicRangeIqr: number;
  lowerFence: number;
  upperFence: number;
  outlierCount: number;
}

export interface SlideNormalizationSummary {
  slideId: string;
  markerCount: number;
  outlierMarkerCount: number;
  dominantMinMarkerId: string;
  dominantMinZScore: number;
  dominantMaxMarkerId: string;
  dominantMaxZScore: number;
  dominantRangeMarkerId: string;
  dominantRangeZScore: number;
  maxAbsoluteZScore: number;
}

export interface MissingNormalizationEntry {
  slideId: string;
  channelKey: string;
  markerId: string;
}

export interface MarkerNormalizationInsights {
  observations: MarkerNormalizationObservation[];
  markerSummaries: MarkerNormalizationSummary[];
  observationsByMarkerId: Record<string, MarkerNormalizationObservation[]>;
  slideSummaries: SlideNormalizationSummary[];
  missingNormalizationEntries: MissingNormalizationEntry[];
  totalChannelSlideCount: number;
}

export interface ChannelNormalizationParams {
  min_c?: unknown;
  max_c?: unknown;
}

interface ChannelNormalizationValue {
  markerType?: string;
  channelNormalizationResults?: {
    params?: ChannelNormalizationParams;
  };
}

interface BaseMarkerNormalizationObservation {
  markerId: string;
  channelKey: string;
  slideId: string;
  experimentResultId: number;
  minValue: number;
  maxValue: number;
  dynamicRange: number;
}

const getFiniteNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  return undefined;
};

export const hasCompleteChannelNormalizationResults = (
  channelNormalizationResults?: { params?: ChannelNormalizationParams } | null
) => {
  const params = channelNormalizationResults?.params;
  return getFiniteNumber(params?.min_c) !== undefined && getFiniteNumber(params?.max_c) !== undefined;
};

const calculateMean = (values: number[]): number => {
  if (!values.length) {
    return 0;
  }

  return sum(values) / values.length;
};

const calculateVariance = (values: number[]): number => {
  if (!values.length) {
    return 0;
  }

  const meanValue = calculateMean(values);

  return sum(map(values, (value) => (value - meanValue) ** 2)) / values.length;
};

const calculateQuantile = (sortedValues: number[], quantile: number): number => {
  if (!sortedValues.length) {
    return 0;
  }

  if (sortedValues.length === 1) {
    return sortedValues[0];
  }

  const index = (sortedValues.length - 1) * quantile;
  const lowerIndex = Math.floor(index);
  const upperIndex = Math.ceil(index);

  if (lowerIndex === upperIndex) {
    return sortedValues[lowerIndex];
  }

  const lowerValue = sortedValues[lowerIndex];
  const upperValue = sortedValues[upperIndex];

  return lowerValue + (upperValue - lowerValue) * (index - lowerIndex);
};

const calculateZScore = (value: number, meanValue: number, standardDeviation: number): number => {
  if (!standardDeviation) {
    return 0;
  }

  return (value - meanValue) / standardDeviation;
};

const buildBaseObservations = (
  normalizationSummary: SlideChannelNormalizationResultSummary[],
  markerIds?: string[],
  missingEntries?: MissingNormalizationEntry[]
): BaseMarkerNormalizationObservation[] => {
  return compact(
    map(normalizationSummary, (slideSummary) => {
      return map(keys(slideSummary.normalizationsSummary || {}), (channelKey) => {
        const normalizationValue = slideSummary.normalizationsSummary[channelKey] as ChannelNormalizationValue;
        const markerId = normalizationValue?.markerType;

        if (!markerId || (markerIds?.length && !includes(markerIds, markerId))) {
          return undefined;
        }

        const params = normalizationValue?.channelNormalizationResults?.params;
        const minValue = getFiniteNumber(params?.min_c);
        const maxValue = getFiniteNumber(params?.max_c);

        if (
          !hasCompleteChannelNormalizationResults(normalizationValue?.channelNormalizationResults) ||
          minValue === undefined ||
          maxValue === undefined
        ) {
          missingEntries?.push({
            slideId: slideSummary.slideId,
            channelKey,
            markerId,
          });
          return undefined;
        }

        return {
          markerId,
          channelKey,
          slideId: slideSummary.slideId,
          experimentResultId: slideSummary.experimentResultId,
          minValue,
          maxValue,
          dynamicRange: maxValue - minValue,
        };
      });
    }).flat()
  );
};

const enrichMarkerObservations = (
  markerObservations: BaseMarkerNormalizationObservation[]
): {
  observations: MarkerNormalizationObservation[];
  summary: MarkerNormalizationSummary;
} => {
  const dynamicRanges = map(markerObservations, 'dynamicRange');
  const minValues = map(markerObservations, 'minValue');
  const maxValues = map(markerObservations, 'maxValue');

  const dynamicRangeMean = calculateMean(dynamicRanges);
  const dynamicRangeVariance = calculateVariance(dynamicRanges);
  const dynamicRangeStdDev = Math.sqrt(dynamicRangeVariance);
  const minMean = calculateMean(minValues);
  const minStdDev = Math.sqrt(calculateVariance(minValues));
  const maxMean = calculateMean(maxValues);
  const maxStdDev = Math.sqrt(calculateVariance(maxValues));

  const sortedDynamicRanges = sortBy(dynamicRanges);
  const dynamicRangeMedian = calculateQuantile(sortedDynamicRanges, 0.5);
  const lowerQuartile = calculateQuantile(sortedDynamicRanges, 0.25);
  const upperQuartile = calculateQuantile(sortedDynamicRanges, 0.75);
  const interQuartileRange = upperQuartile - lowerQuartile;
  const lowerFence = lowerQuartile - interQuartileRange * 1.5;
  const upperFence = upperQuartile + interQuartileRange * 1.5;

  const observations = map(markerObservations, (observation) => {
    const minZScore = calculateZScore(observation.minValue, minMean, minStdDev);
    const maxZScore = calculateZScore(observation.maxValue, maxMean, maxStdDev);
    const rangeZScore = calculateZScore(observation.dynamicRange, dynamicRangeMean, dynamicRangeStdDev);
    const isOutlier = observation.dynamicRange < lowerFence || observation.dynamicRange > upperFence;

    return {
      ...observation,
      minZScore,
      maxZScore,
      rangeZScore,
      absoluteMaxZScore: max(map([minZScore, maxZScore, rangeZScore], (value) => Math.abs(value))) || 0,
      isOutlier,
    };
  });

  const summary: MarkerNormalizationSummary = {
    markerId: markerObservations[0].markerId,
    slideCount: markerObservations.length,
    minCurrentValue: min(minValues) ?? 0,
    maxCurrentValue: max(maxValues) ?? 0,
    dynamicRangeMin: min(dynamicRanges) ?? 0,
    dynamicRangeMax: max(dynamicRanges) ?? 0,
    dynamicRangeMedian,
    dynamicRangeMean,
    dynamicRangeVariance,
    dynamicRangeStdDev,
    dynamicRangeLowerQuartile: lowerQuartile,
    dynamicRangeUpperQuartile: upperQuartile,
    dynamicRangeIqr: interQuartileRange,
    lowerFence,
    upperFence,
    outlierCount: observations.filter((observation) => observation.isOutlier).length,
  };

  return { observations, summary };
};

const buildSlideNormalizationSummaries = (
  observations: MarkerNormalizationObservation[]
): SlideNormalizationSummary[] => {
  const groupedObservations = groupBy(observations, 'slideId');

  return orderBy(
    compact(
      map(keys(groupedObservations), (slideId) => {
        const slideObservations = groupedObservations[slideId];

        if (!slideObservations?.length) {
          return undefined;
        }

        const dominantMinObservation = orderBy(
          slideObservations,
          [(observation) => Math.abs(observation.minZScore), 'markerId'],
          ['desc', 'asc']
        )[0];
        const dominantMaxObservation = orderBy(
          slideObservations,
          [(observation) => Math.abs(observation.maxZScore), 'markerId'],
          ['desc', 'asc']
        )[0];
        const dominantRangeObservation = orderBy(
          slideObservations,
          [(observation) => Math.abs(observation.rangeZScore), 'markerId'],
          ['desc', 'asc']
        )[0];

        return {
          slideId,
          markerCount: slideObservations.length,
          outlierMarkerCount: slideObservations.filter((observation) => observation.isOutlier).length,
          dominantMinMarkerId: dominantMinObservation.markerId,
          dominantMinZScore: dominantMinObservation.minZScore,
          dominantMaxMarkerId: dominantMaxObservation.markerId,
          dominantMaxZScore: dominantMaxObservation.maxZScore,
          dominantRangeMarkerId: dominantRangeObservation.markerId,
          dominantRangeZScore: dominantRangeObservation.rangeZScore,
          maxAbsoluteZScore:
            max(
              map(
                [
                  Math.abs(dominantMinObservation.minZScore),
                  Math.abs(dominantMaxObservation.maxZScore),
                  Math.abs(dominantRangeObservation.rangeZScore),
                ],
                (value) => value
              )
            ) ?? 0,
        };
      })
    ),
    ['outlierMarkerCount', 'maxAbsoluteZScore', 'slideId'],
    ['desc', 'desc', 'asc']
  );
};

export const buildMarkerNormalizationInsights = (
  normalizationSummary: SlideChannelNormalizationResultSummary[],
  markerIds?: string[]
): MarkerNormalizationInsights => {
  const missingNormalizationEntries: MissingNormalizationEntry[] = [];
  const groupedObservations = groupBy(
    buildBaseObservations(normalizationSummary, markerIds, missingNormalizationEntries),
    'markerId'
  );
  const observationsByMarkerId: Record<string, MarkerNormalizationObservation[]> = {};
  const observations = compact(
    map(keys(groupedObservations), (markerId) => {
      const markerObservations = groupedObservations[markerId];

      if (!markerObservations?.length) {
        return [];
      }

      const { observations: enrichedObservations } = enrichMarkerObservations(markerObservations);
      observationsByMarkerId[markerId] = orderBy(
        enrichedObservations,
        [(observation) => (observation.isOutlier ? 1 : 0), 'absoluteMaxZScore', 'slideId'],
        ['desc', 'desc', 'asc']
      );

      return enrichedObservations;
    }).flat()
  );
  const markerSummaries = compact(
    map(keys(observationsByMarkerId), (markerId) => {
      const markerObservations = groupedObservations[markerId];

      if (!markerObservations?.length) {
        return undefined;
      }

      const { summary } = enrichMarkerObservations(markerObservations);

      return summary;
    })
  );

  return {
    observations: orderBy(observations, ['markerId', 'slideId'], ['asc', 'asc']),
    markerSummaries: orderBy(markerSummaries, ['markerId'], ['asc']),
    observationsByMarkerId,
    slideSummaries: buildSlideNormalizationSummaries(observations),
    missingNormalizationEntries: orderBy(missingNormalizationEntries, ['slideId', 'markerId', 'channelKey']),
    totalChannelSlideCount: observations.length + missingNormalizationEntries.length,
  };
};
